import { useEffect } from "react";
import {
  api,
  ApiNames,
  ApiCallerMap,
  CallerType,
  getFinancialNavigationPotentialSavingPhis,
  postPortalDataThroughChromeExtension
} from "../api/api";
import { getAuthInstance } from "@tailormed/common-client/services/auth";
import { oktaConfig } from "../constant/okta";

const ChromeExtensionListener = () => {
  // Helper function to get API method with proper authentication
  function getApiMethod(apiName) {
    if (ApiCallerMap[apiName] === CallerType.Auth) {
      const token = getAuthInstance(oktaConfig.redirectUri).getAccessToken();
      return (...params) => api(apiName)(token, ...params);
    }

    return api(apiName);
  }

  useEffect(() => {
    // Function to fetch patient data by ID only and resolve papId based on assistance program tab URL
    const fetchPatientDataByIdForExtension = async (patientId, assistanceProgramTabUrl) => {
      try {
        // Fetch patient details using the correct API pattern
        console.log("fetchPatientDataByIdForExtension", patientId, assistanceProgramTabUrl);
        const patientDetailsApiMethod = getApiMethod(ApiNames.PatientDetails);
        const patientDetailsResponse = await patientDetailsApiMethod(patientId);

        // The response structure is {financial-navigation-patient-phis: [patientData]} - array with one element
        const patientDetails =
          patientDetailsResponse["financial-navigation-patient-phis"]?.[0] ||
          patientDetailsResponse["financial-navigation-patients"]?.[0];

        if (!patientDetails) {
          throw new Error(`Patient with ID ${patientId} not found`);
        }

        // Fetch journey data using patient's journey ID
        const journeyId = patientDetails?.journeyId || patientDetails?.journey;

        if (!journeyId) {
          throw new Error(`No journey found for patient ${patientId}`);
        }

        const patientJourneyApiMethod = getApiMethod(ApiNames.PatientJourney);
        const patientJourneyResponse = await patientJourneyApiMethod(journeyId);

        // Same pattern - likely an array with one element
        const patientJourney =
          patientJourneyResponse["financial-navigation-patient-journey-phis"]?.[0] ||
          patientJourneyResponse["financial-navigation-patient-journey"]?.[0];

        if (!patientJourney) {
          throw new Error(`Journey with ID ${journeyId} not found`);
        }

        // Get all potential savings for this patient using the correct property structure
        const { allPotentialSavingsGroupedByType } = patientJourney || {};
        if (!allPotentialSavingsGroupedByType) {
          throw new Error(`No potential savings found for patient ${patientId}`);
        }

        // Flatten the grouped potential savings like in selector.js
        const allPotentialSavingsFlattened = Object.values(allPotentialSavingsGroupedByType).flat();
        const potentialSavingsIds = allPotentialSavingsFlattened.map((ps) => ps.potentialSavingId);

        if (potentialSavingsIds.length === 0) {
          throw new Error(`No potential savings found for patient ${patientId}`);
        }

        // Fetch all potential savings details
        const potentialSavingsResponse = await getFinancialNavigationPotentialSavingPhis(potentialSavingsIds);
        const allPotentialSavings =
          potentialSavingsResponse.data?.["financial-navigation-potential-saving-phi"] ||
          potentialSavingsResponse["financial-navigation-potential-saving-phi"] ||
          [];

        if (!allPotentialSavings.length) {
          throw new Error(`No potential savings details found for patient ${patientId}`);
        }

        // Extract base URL from assistance program tab URL for matching (if available)
        let matchingSavings = [];

        if (assistanceProgramTabUrl) {
          try {
            const assistanceUrl = new URL(assistanceProgramTabUrl);
            const assistanceBaseDomain = assistanceUrl.hostname;

            // Find potential savings that match the assistance program tab's URL
            matchingSavings = allPotentialSavings.filter((saving) => {
              // Check multiple website properties that might contain URLs
              const websiteFields = [saving?.website, saving?.pharmacyWebsite, saving?.url].filter(Boolean);

              return websiteFields.some((websiteUrl) => {
                try {
                  const websiteHostname = new URL(websiteUrl).hostname;
                  // Check if assistance program domain contains the website domain or vice versa
                  return (
                    assistanceBaseDomain.includes(websiteHostname) || websiteHostname.includes(assistanceBaseDomain)
                  );
                } catch {
                  // If URL parsing fails, try simple string matching
                  return websiteUrl.includes(assistanceBaseDomain) || assistanceBaseDomain.includes(websiteUrl);
                }
              });
            });
          } catch (error) {
            console.warn("Error parsing assistance program tab URL:", assistanceProgramTabUrl, error);
            matchingSavings = []; // No matches if URL parsing fails
          }
        } else {
          console.log("No assistance program tab URL provided - skipping URL matching");
          matchingSavings = []; // No matches if no URL provided
        }

        // Selection logic based on URL availability and matches
        if (!assistanceProgramTabUrl) {
          // Case 1: No assistanceProgramTabUrl - ask user to select
          const savingsOptions = allPotentialSavings.map((saving) => ({
            id: saving?.id,
            name: saving?.name,
            description: saving?.description || "",
            apProvider: saving?.APProvider || "",
            drugNames: saving?.approvedRxnormDrugs?.map((drug) => drug?.tmDisplayName).join(", ") || ""
          }));

          window.postMessage(
            {
              type: "CHROME_EXTENSION_RESPONSE",
              message: "pap_selection_required",
              patientDetails,
              patientJourney,
              savingsOptions,
              assistanceProgramTabUrl: null,
              success: true
            },
            "*"
          );
        } else if (matchingSavings.length === 1) {
          // Case 2: Has URL and exactly 1 match - auto-select it
          const potentialSaving = matchingSavings[0];

          window.postMessage(
            {
              type: "CHROME_EXTENSION_RESPONSE",
              message: "patient_data_response",
              patientDetails,
              patientJourney,
              potentialSaving,
              success: true
            },
            "*"
          );
        } else {
          // Case 3 & 4: Has URL but 0 matches OR multiple matches - ask user to select
          const savingsOptions = allPotentialSavings.map((saving) => ({
            id: saving?.id,
            name: saving?.name,
            description: saving?.description || "",
            apProvider: saving?.APProvider || "",
            drugNames: saving?.approvedRxnormDrugs?.map((drug) => drug?.tmDisplayName).join(", ") || ""
          }));

          window.postMessage(
            {
              type: "CHROME_EXTENSION_RESPONSE",
              message: "pap_selection_required",
              patientDetails,
              patientJourney,
              savingsOptions,
              assistanceProgramTabUrl,
              success: true
            },
            "*"
          );
        }
      } catch (error) {
        // Send error response back to extension
        window.postMessage(
          {
            type: "CHROME_EXTENSION_RESPONSE",
            message: "patient_data_error",
            errorMessage: error?.message || "Unknown error occurred",
            success: false
          },
          "*"
        );
      }
    };

    // Listen for messages from the extension content script
    const messageHandler = async (event) => {
      // Only handle messages from the same window (content script)
      if (event?.source !== window) {
        return;
      }

      if (event?.data?.type === "CHROME_EXTENSION_MESSAGE") {
        if (event?.data?.message === "fetch_patient_data_by_id") {
          const { patientId, assistanceProgramTabUrl } = event?.data || {};
          if (patientId) {
            fetchPatientDataByIdForExtension(patientId, assistanceProgramTabUrl);
          }
        }
      }

      // Handle send_data_to_server messages from chrome extension
      if (event?.data?.message === "send_data_to_server") {
        try {
          const { data } = event?.data || {};
          if (data) {
            console.log("ChromeExtensionListener received send_data_to_server:", data);
            await postPortalDataThroughChromeExtension({ data });
            console.log("Successfully sent portal data to server");
          } else {
            console.warn("ChromeExtensionListener received send_data_to_server but no data found");
          }
        } catch (error) {
          console.error("Failed to send portal data to server:", error);
        }
      }
    };

    window.addEventListener("message", messageHandler);

    return () => {
      window.removeEventListener("message", messageHandler);
    };
  }, []);

  return null; // This component doesn't render anything
};

export { ChromeExtensionListener };
